'use client';

import Link from 'next/link';
import { useContext } from 'react';
import { useTranslation } from '@root/src/i18n/client';
import { DataContextCart } from '@root/src/contextcart';
import ProductCard from './ProductCard';

interface Product {
  id: string;
  name: string;
  slug: string;
  image: string | string[];
  retailPrice: number;
  discount?: number;
  onPromotion?: boolean;
  status: boolean;
  totalQuantity: number;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  href?: string;
}

interface ProductCategoryRowProps {
  category: Category;
  products: Product[];
}

const getProductImage = (image?: string | string[]) =>
  Array.isArray(image) ? image[0] || '' : image || '';

export default function ProductCategoryRow({ category, products }: ProductCategoryRowProps) {
  const contextCart: any = useContext(DataContextCart);
  const { dataCart = [], updateCart } = contextCart || {};
  const { t } = useTranslation('common');

  console.log("Rendering ProductCategoryRow for category:", category);
  console.log("Products passed to ProductCategoryRow:", products);

  const addToCart = (product: Product) => {
    const productImage = getProductImage(product.image);
    const currentCart = [...dataCart];
    const newProduct = {
      productId: product.id,
      productName: product.name,
      productPrice: product.retailPrice,
      discount: product.discount || 0,
      productImage,
      quantity: 1,
    };
    const existingIndex = currentCart.findIndex(
      item => item.productId === newProduct.productId
    );
    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity += 1;
    } else {
      currentCart.push(newProduct);
    }
    updateCart?.(currentCart);
  };

  // If server provided products may not include `status`, prefer server list.
  const hasStatusField = products.some(p => p && Object.prototype.hasOwnProperty.call(p, 'status'));
  const activeProducts = hasStatusField
    ? products.filter(p => p.status === true).slice(0, 5)
    : products.slice(0, 5);

  if (activeProducts.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-[#EDE6D8] overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr]">

        {/* Left: Category Info */}
        <div className="flex flex-col justify-center gap-4 border-b border-[#EDE6D8] bg-[#FDFAF5] p-5 lg:border-b-0 lg:border-r lg:p-6">
   
          <div className="text-[#223A27]">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c0-5.5-4.5-10-10-10 5.5 0 10-4.5 10-10z"/>
              <path d="M12 2c5.5 0 10 4.5 10 10S17.5 22 12 22c0-5.5 4.5-10 10-10-5.5 0-10-4.5-10-10z"/>
            </svg>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-bold leading-tight text-[#223A27] sm:text-xl">
              {category.name}
            </h2>
            {category.description && (
              <p className="text-xs text-[#6B6656] leading-relaxed line-clamp-3 max-w-[220px]">
                {category.description}
              </p>
            )}
          </div>

          <Link
            href={category.href || `/collection/${category.slug}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#E7E1D6] bg-[#FDFAF5] px-4 py-2 text-xs font-semibold text-[#223A27] transition-colors duration-200 hover:bg-[#223A27] hover:text-[#FDFAF5] w-fit"
          >
            {t('lytra.exploreMore')}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </Link>
        </div>

        {/* Right: Products Grid */}
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
            {activeProducts.map(product => (
              <ProductCard
                key={product.id}
                id={product.id}
                name={product.name}
                slug={product.slug}
                image={getProductImage(product.image)}
                price={product.retailPrice}
                discount={product.discount}
                onPromotion={product.onPromotion}
                onAddToCart={() => addToCart(product)}
                totalQuantity={product.totalQuantity || 0}
              />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
